import { useHandler } from "@redotech/react-util/hook";
import { type Translate, useTranslation } from "@redotech/redo-i18n";
import {
  isUpsDamageInfoComplete,
  UpsClaimInfo,
  UpsDamageInfo,
  UpsDamageLocation,
  UpsItemCondition,
  UpsPackageType,
  UpsPackagingMaterial,
  UpsSealMethod,
} from "@redotech/redo-package-protection-common/shipping-claims/carrier-claim-info/ups";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";

function enumOptions<T extends string>(
  values: T[],
  label: (value: T) => string,
): RedoListItem<T>[] {
  return values.map((v) => ({ type: "text", text: label(v), value: v, id: v }));
}

function getDamageLocationLabel(location: UpsDamageLocation, t: Translate) {
  switch (location) {
    case UpsDamageLocation.AT_SHIPPER:
      return t("At shipper's location");
    case UpsDamageLocation.AT_CONSIGNEE:
      return t("At consignee's location");
    case UpsDamageLocation.AT_OTHER:
      return t("At other location");
  }
}

function getItemConditionLabel(condition: UpsItemCondition, t: Translate) {
  switch (condition) {
    case UpsItemCondition.BENT:
      return t("Bent");
    case UpsItemCondition.CRACKED:
      return t("Cracked");
    case UpsItemCondition.CRUSHED:
      return t("Crushed");
    case UpsItemCondition.DENTED:
      return t("Dented");
    case UpsItemCondition.PUNCTURE:
      return t("Puncture");
    case UpsItemCondition.SCRATCHED:
      return t("Scratched");
    case UpsItemCondition.SHATTERED:
      return t("Shattered");
    case UpsItemCondition.SOILED:
      return t("Soiled");
    case UpsItemCondition.SPOILED:
      return t("Spoiled");
    case UpsItemCondition.TORN:
      return t("Torn");
    case UpsItemCondition.WET:
      return t("Wet");
    case UpsItemCondition.OTHER:
      return t("Other", { context: "damaged item condition" });
  }
}

function getPackageTypeLabel(packageType: UpsPackageType, t: Translate) {
  switch (packageType) {
    case UpsPackageType.BOX:
      return t("Box");
    case UpsPackageType.BAG:
      return t("Bag");
    case UpsPackageType.CASE:
      return t("Case");
    case UpsPackageType.ENVELOPE:
      return t("Envelope");
    case UpsPackageType.PAIL:
      return t("Pail/bucket/can");
    case UpsPackageType.WRAP:
      return t("Plastic or paper wrap");
    case UpsPackageType.TUBE:
      return t("Tube");
    case UpsPackageType.OTHER:
      return t("Other", { context: "package type" });
  }
}

function getSealMethodLabel(method: UpsSealMethod, t: Translate) {
  switch (method) {
    case UpsSealMethod.CABLE:
      return t("Cable");
    case UpsSealMethod.CAP:
      return t("Cap");
    case UpsSealMethod.CLASP:
      return t("Clasp");
    case UpsSealMethod.GLUE:
      return t("Glue");
    case UpsSealMethod.HINGE:
      return t("Hinge");
    case UpsSealMethod.LATCH:
      return t("Latch");
    case UpsSealMethod.NAILS:
      return t("Nails");
    case UpsSealMethod.NONE:
      return t("None", { context: "package seal method" });
    case UpsSealMethod.STAPLES:
      return t("Staples");
    case UpsSealMethod.STITCH:
      return t("Stitch");
    case UpsSealMethod.STRAPS:
      return t("Straps");
    case UpsSealMethod.STRING:
      return t("String");
    case UpsSealMethod.TAPE:
      return t("Tape");
    case UpsSealMethod.TIES:
      return t("Ties");
    case UpsSealMethod.TWIST:
      return t("Twist");
    case UpsSealMethod.ZIPPER:
      return t("Zipper");
  }
}

function getPackagingMaterialLabel(
  material: UpsPackagingMaterial,
  t: Translate,
) {
  switch (material) {
    case UpsPackagingMaterial.AIR_BAGS:
      return t("Air bags");
    case UpsPackagingMaterial.WRAP:
      return t("Wrap");
    case UpsPackagingMaterial.CORRUGATED:
      return t("Corrugated");
    case UpsPackagingMaterial.DOUBLE_BOXED:
      return t("Double boxed");
    case UpsPackagingMaterial.FOAM:
      return t("Foam");
    case UpsPackagingMaterial.LOOSE_FILL:
      return t("Loose fill");
    case UpsPackagingMaterial.PAPER:
      return t("Paper");
    case UpsPackagingMaterial.PEANUTS:
      return t("Peanuts", { context: "packing material" });
    case UpsPackagingMaterial.NONE:
      return t("None", { context: "packaging material" });
    case UpsPackagingMaterial.OTHER:
      return t("Other", { context: "packaging material" });
  }
}

export type PartialUpsDamageInfo = Partial<UpsDamageInfo>;

export function buildUpsClaimInfo(
  d: PartialUpsDamageInfo,
): UpsClaimInfo | null {
  if (!isUpsDamageInfoComplete(d)) {
    return null;
  }
  return { damage: d };
}

interface UpsClaimInfoSectionProps {
  value: PartialUpsDamageInfo;
  setValue: (next: PartialUpsDamageInfo) => void;
}

export const UpsClaimInfoSection = memo(function UpsClaimInfoSection({
  value,
  setValue,
}: UpsClaimInfoSectionProps) {
  const { t } = useTranslation();
  const update = useHandler((patch: Partial<UpsDamageInfo>) => {
    setValue({ ...value, ...patch });
  });

  const dateString = useMemo(
    () =>
      value.dateDamageDiscovered
        ? formatDateForInput(value.dateDamageDiscovered)
        : "",
    [value.dateDamageDiscovered],
  );

  const {
    locationOptions,
    itemConditionOptions,
    packageTypeOptions,
    sealMethodOptions,
    packagingMaterialOptions,
  } = useUpsDamageSelectOptions();

  return (
    <Flex dir="column" gap="md">
      <Text fontSize="sm" fontWeight="semibold">
        {t("UPS damage details")}
      </Text>

      <YesNoField
        label={t("Are both contents and packaging available for inspection?")}
        setValue={(v) => update({ contentsAndPackagingAvailable: v })}
        value={value.contentsAndPackagingAvailable}
      />

      <RedoTextInput
        inputTypeHint="date"
        label={t("Date damage was discovered")}
        setValue={(s) => {
          if (!s) {
            update({ dateDamageDiscovered: undefined });
            return;
          }
          const d = new Date(s);
          if (!isNaN(d.getTime())) {
            update({ dateDamageDiscovered: d });
          }
        }}
        value={dateString}
      />

      <RedoTextInput
        inputTypeHint="number"
        label={t("Total items in the package")}
        max={9999}
        min={1}
        setValue={(s) => {
          const n = parseInt(s, 10);
          update({ totalItemsInPackage: isNaN(n) ? undefined : n });
        }}
        value={
          value.totalItemsInPackage !== undefined
            ? String(value.totalItemsInPackage)
            : ""
        }
      />

      <YesNoField
        label={t("Are items missing from the package?")}
        setValue={(v) => update({ itemsMissing: v })}
        value={value.itemsMissing}
      />

      <YesNoField
        label={t("Contains dangerous goods?")}
        setValue={(v) => update({ containsDangerousGoods: v })}
        value={value.containsDangerousGoods}
      />

      <YesNoField
        label={t("Can the package be sealed and transported safely?")}
        setValue={(v) => update({ canBeSealedSafely: v })}
        value={value.canBeSealedSafely}
      />

      <RedoSingleSelectDropdownInput
        label={t("Current location of the damaged package")}
        options={locationOptions}
        optionSelected={(item) => update({ currentLocation: item.item.value })}
        selectedItem={locationOptions.find(
          (o) => o.value === value.currentLocation,
        )}
      />

      {value.currentLocation === UpsDamageLocation.AT_OTHER && (
        <Flex dir="column" gap="sm">
          <RedoTextInput
            label={t("Other location: contact name")}
            maxLength={35}
            setValue={(s) => update({ otherLocationContactName: s })}
            value={value.otherLocationContactName ?? ""}
          />
          <RedoTextInput
            label={t("Other location: city")}
            maxLength={50}
            setValue={(s) => update({ otherLocationCity: s })}
            value={value.otherLocationCity ?? ""}
          />
          <RedoTextInput
            label={t("Other location: postal code")}
            maxLength={8}
            setValue={(s) => update({ otherLocationPostal: s })}
            value={value.otherLocationPostal ?? ""}
          />
          <RedoTextInput
            label={t("Other location: country code")}
            maxLength={2}
            setValue={(s) => update({ otherLocationCountry: s })}
            value={value.otherLocationCountry ?? ""}
          />
        </Flex>
      )}

      <RedoSingleSelectDropdownInput
        label={t("Condition of damaged items")}
        options={itemConditionOptions}
        optionSelected={(item) => update({ itemCondition: item.item.value })}
        selectedItem={itemConditionOptions.find(
          (o) => o.value === value.itemCondition,
        )}
      />

      {value.itemCondition === UpsItemCondition.OTHER && (
        <RedoTextareaInputField
          label={t("Describe the item condition")}
          maxLength={50}
          setValue={(s) => update({ itemConditionOther: s })}
          value={value.itemConditionOther ?? ""}
        />
      )}

      <RedoSingleSelectDropdownInput
        label={t("Package type")}
        options={packageTypeOptions}
        optionSelected={(item) => update({ packageType: item.item.value })}
        selectedItem={packageTypeOptions.find(
          (o) => o.value === value.packageType,
        )}
      />

      {value.packageType === UpsPackageType.OTHER && (
        <RedoTextInput
          label={t("Describe the package type")}
          maxLength={50}
          setValue={(s) => update({ packageTypeOther: s })}
          value={value.packageTypeOther ?? ""}
        />
      )}

      <YesNoField
        label={t("Damage to outside of package?")}
        setValue={(v) => update({ damageToOutside: v })}
        value={value.damageToOutside}
      />

      <RedoSingleSelectDropdownInput
        label={t("How was the package sealed?")}
        options={sealMethodOptions}
        optionSelected={(item) => update({ howPackageSealed: item.item.value })}
        selectedItem={sealMethodOptions.find(
          (o) => o.value === value.howPackageSealed,
        )}
      />

      <RedoSingleSelectDropdownInput
        label={t("Packaging materials")}
        options={packagingMaterialOptions}
        optionSelected={(item) =>
          update({ packagingMaterials: item.item.value })
        }
        selectedItem={packagingMaterialOptions.find(
          (o) => o.value === value.packagingMaterials,
        )}
      />

      {value.packagingMaterials === UpsPackagingMaterial.OTHER && (
        <RedoTextInput
          label={t("Describe the packaging materials")}
          maxLength={50}
          setValue={(s) => update({ packagingMaterialsOther: s })}
          value={value.packagingMaterialsOther ?? ""}
        />
      )}
    </Flex>
  );
});

interface YesNoFieldProps {
  label: string;
  value: boolean | undefined;
  setValue: (next: boolean) => void;
}

const YesNoField = memo(function YesNoField({
  label,
  value,
  setValue,
}: YesNoFieldProps) {
  const options = useYesNoOptions();
  return (
    <RedoSingleSelectDropdownInput
      label={label}
      options={options}
      optionSelected={(item) => setValue(item.item.value)}
      selectedItem={options.find((o) => o.value === value)}
    />
  );
});

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function formatDateForInput(d: Date): string {
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

function useUpsDamageSelectOptions() {
  const { t } = useTranslation();
  const locationOptions = useMemo(
    () =>
      enumOptions(Object.values(UpsDamageLocation), (value) =>
        getDamageLocationLabel(value, t),
      ),
    [t],
  );
  const itemConditionOptions = useMemo(
    () =>
      enumOptions(Object.values(UpsItemCondition), (value) =>
        getItemConditionLabel(value, t),
      ),
    [t],
  );
  const packageTypeOptions = useMemo(
    () =>
      enumOptions(Object.values(UpsPackageType), (value) =>
        getPackageTypeLabel(value, t),
      ),
    [t],
  );
  const sealMethodOptions = useMemo(
    () =>
      enumOptions(Object.values(UpsSealMethod), (value) =>
        getSealMethodLabel(value, t),
      ),
    [t],
  );
  const packagingMaterialOptions = useMemo(
    () =>
      enumOptions(Object.values(UpsPackagingMaterial), (value) =>
        getPackagingMaterialLabel(value, t),
      ),
    [t],
  );
  return {
    locationOptions,
    itemConditionOptions,
    packageTypeOptions,
    sealMethodOptions,
    packagingMaterialOptions,
  };
}

function useYesNoOptions(): RedoListItem<boolean>[] {
  const { t } = useTranslation();
  return useMemo(
    () => [
      { type: "text", text: t("Yes"), value: true, id: "yes" },
      { type: "text", text: t("No"), value: false, id: "no" },
    ],
    [t],
  );
}
